import React, { useEffect, useRef, useState } from 'react';
import { useOutsideClickEffect } from '@saviynt/common';

import { useIspmContext } from '../../context/ispmContext';
import {
  type GetIspmSuggestionsResponse,
  getSaviSuggestion,
  type Suggestion,
} from '../../data-layer/savi-suggestions';
import { getSaviApiUrlLists, Logger } from '../../services';
import { SUGGESTIONS } from '../../tests/testids';
import { useLocalize } from '../../utils/localization';

import SaviSuggestionItem from './SaviSuggestionItem/SaviSuggestionItem';

import './SaviSuggestions.css';

function SaviSuggestions(): JSX.Element | null {
  // ISPM related context values
  const { ispmState, updateIspmState } = useIspmContext();
  const suggestionRef = useRef(null);
  const [sessionData, setSessionData] =
    useState<GetIspmSuggestionsResponse | null>(() => {
      // Retrieve data from session storage if available
      const storedData = sessionStorage.getItem('Ispm-Suggestions');

      return storedData
        ? (JSON.parse(storedData) as GetIspmSuggestionsResponse)
        : null;
    });

  const { CHAT_SUGGESTIONS_URL } = getSaviApiUrlLists(
    ispmState.useMockServerApis
  );

  const [isLoading, setIsLoading] = useState<boolean>(false); // loading state
  const [isError, setIsError] = useState<string | null>(null); // error state
  const [suggestionData, setSuggestionData] =
    useState<GetIspmSuggestionsResponse | null>(null); // error state

  const handleBlur = () => {
    updateIspmState('isSuggestionVisible', false);
  };

  const localize = useLocalize();

  useEffect(() => {
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('blur', handleBlur);
    };
  });

  useEffect(() => {
    // Function to fetch suggestion
    const fetchSuggestion = async () => {
      if (sessionData) {
        return;
      }

      try {
        setIsLoading(true); // set loading to true when API call starts
        const fetchedData = await getSaviSuggestion(CHAT_SUGGESTIONS_URL);

        setSuggestionData(fetchedData);
      } catch (err) {
        setIsError('Error fetching suggestions');
        Logger.error(err);
      } finally {
        setIsLoading(false); // turn off loading state once done
      }
    };

    fetchSuggestion()
      .then((data) => {
        Logger.info('savi suggestion', { data });
      })
      .catch((err) => {
        Logger.error('Error in SaviSuggestion call', err);
      }); // Call the function when the component mounts
  }, [CHAT_SUGGESTIONS_URL, sessionData]); // Empty dependency array to call the function only on component mount

  useEffect(() => {
    // Store fetched data in sessionStorage
    if (suggestionData && !sessionData) {
      sessionStorage.setItem(
        'Ispm-Suggestions',
        JSON.stringify(suggestionData)
      );
      setSessionData(suggestionData);
    }
  }, [sessionData, suggestionData]);

  const handleClickOutside = () => {
    updateIspmState('isSuggestionVisible', false);
  };

  useOutsideClickEffect(suggestionRef, handleClickOutside, true);

  const handleSuggestionClick = (text: string) => {
    Logger.info('Handling suggestion click', { text });
    updateIspmState('isChatOverlayVisible', true);
    updateIspmState('isSuggestionVisible', false);
    updateIspmState('userQuery', text);
  };

  // Make suggestion api in background. We store this data irrespective of ispmState.isSuggestionVisible
  if (
    isLoading ||
    isError ||
    !ispmState.isSuggestionVisible ||
    !Array.isArray(sessionData?.suggestions)
  ) {
    // Don't display anything
    return null;
  }

  return (
    <section
      className='Ispm-SaviSuggestions'
      ref={suggestionRef}
      data-testid={SUGGESTIONS}>
      {sessionData.suggestions.map((ele: Suggestion) => (
        <div key={ele.id} className='Ispm-SaviSuggestionsChild'>
          <SaviSuggestionItem
            suggestion={localize({ id: ele.id, defaultMessage: ele.text })}
            onClick={handleSuggestionClick}
            testId={`${SUGGESTIONS}-${ele.id}`}
          />
        </div>
      ))}
    </section>
  );
}

export default SaviSuggestions;
